import { getStripeSessionHeaders } from '@/utils/stripe';
import { useState, useEffect, useRef } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getStripePublishableKey } from "@/config/stripe";
import { useLocation } from "react-router-dom";
import logger from "@/utils/logger";
import {
  ALREADY_PURCHASED,
  CHECKOUT_NOT_READY,
  PAYMENT_FAILED,
  PAYMENT_PENDING,
  TERMS_HINT,
  describePaymentError,
  isAlreadyPurchased,
} from "@/utils/payment-errors";
import { phone } from "phone";

const formatSubscriptionPrice = (amount, currency = "AUD") => {
  if (amount === undefined || amount === null) return null;
  const num = Number(amount);
  if (Number.isNaN(num)) return null;
  const normalized = num >= 100 ? num / 100 : num;
  return normalized.toLocaleString("en-AU", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const normalizePhoneForStripe = (phoneNumber) => {
  if (!phoneNumber || typeof phoneNumber !== "string") return phoneNumber || "";
   try {
    const validatedPhoneNumber = phone(phoneNumber);
    logger.log('Phone number validated. E.164 format:', JSON.stringify(validatedPhoneNumber, null, 2));
    return validatedPhoneNumber.phoneNumber;
  } catch (lookupError) {
    logger.error('Phone lookup failed:', lookupError.message);
    return phoneNumber;
  }
};

const initialPublishableKey = getStripePublishableKey();
const initialStripePromise = initialPublishableKey ? loadStripe(initialPublishableKey) : null;

function CheckoutForm({ email, productName, price, clientSecret, onSuccess, onError, onCancel, isLoading: parentLoading, hideBackButton, beforeConfirm }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsHint, setTermsHint] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [paymentPending, setPaymentPending] = useState(false);
  const submitting = useRef(false);
  const termsRef = useRef(null);

  const handlePaymentResult = ({ error, paymentIntent }) => {
    if (error) {
      setPaymentError(describePaymentError(error));
      onError?.(error.message || PAYMENT_FAILED);
      return;
    }
    if (paymentIntent?.status === "succeeded") {
      setPaymentPending(false);
      setPaymentError("");
      setPaymentSuccess(true);
      onSuccess?.(paymentIntent);
    } else if (["processing", "requires_capture"].includes(paymentIntent?.status)) {
      setPaymentPending(true);
      setPaymentError(PAYMENT_PENDING);
    } else {
      setPaymentPending(false);
      setPaymentError(PAYMENT_FAILED);
      onError?.(PAYMENT_FAILED);
    }
  };

  const checkPaymentStatus = async () => {
    if (submitting.current || !stripe) return;
    submitting.current = true;
    setLoading(true);
    try {
      handlePaymentResult(await stripe.retrievePaymentIntent(clientSecret));
    } catch {
      setPaymentError("We couldn't check your payment. Please check its status again before paying.");
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || parentLoading || paymentSuccess || paymentPending || submitting.current) return;

    // Pay stays tappable so the buyer learns why it won't go through
    if (!termsAccepted) {
      setTermsHint(TERMS_HINT);
      termsRef.current?.focus();
      return;
    }

    submitting.current = true;
    setLoading(true);
    setPaymentError("");

    try {
      await beforeConfirm?.();
    } catch (error) {
      setPaymentError(CHECKOUT_NOT_READY);
      onError?.(error?.message || "Account setup failed before payment");
      submitting.current = false;
      setLoading(false);
      return;
    }

    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          receipt_email: email || undefined,
        },
        redirect: "if_required",
      });

      handlePaymentResult(result);
    } catch {
      // A network failure can hide a successful charge. Check the same intent
      // before allowing another confirmation; never create a second checkout.
      setPaymentPending(true);
      setPaymentError(PAYMENT_PENDING);
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Order Summary */}
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 space-y-3 border border-slate-200">
        <div className="flex justify-between text-base">
          <span className="text-slate-600 font-medium">{productName}</span>
          <span className="font-semibold text-slate-900">{price}</span>
        </div>
        <div className="flex justify-between text-base pt-3 border-t border-slate-300">
          <span className="font-bold text-slate-900">Total Due Today</span>
          <span className="font-bold text-xl text-pink-600">{price}</span>
        </div>
      </div>

      {/* Stripe Payment Element */}
      <PaymentElement id="onboarding-payment-element" />

      {/* Email Confirmation */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900 font-medium">
          Confirmation will be sent to <strong>{email}</strong>
        </p>
      </div>

      {/* Terms and Conditions */}
      <div className="flex items-start gap-3">
        <input
          ref={termsRef}
          type="checkbox"
          id="terms-checkbox"
          checked={termsAccepted}
          onChange={(e) => {
            setTermsAccepted(e.target.checked);
            if (e.target.checked) setTermsHint("");
          }}
          aria-describedby={termsHint ? "terms-hint" : undefined}
          className="mt-0.5 w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
        />
        <label htmlFor="terms-checkbox" className="text-sm text-slate-700">
          I agree to the{" "}
          <a
            href="/subscription-agreement"
            target="_blank"
            rel="noopener noreferrer"
            className="text-pink-600 hover:text-pink-700 underline"
          >
            Terms and Conditions
          </a>
        </label>
      </div>
      {termsHint && (
        <p id="terms-hint" role="alert" className="-mt-3 text-sm text-red-600">
          {termsHint}
        </p>
      )}

      {paymentError && (
        <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded-lg">
          {paymentError}
        </div>
      )}

      {/* Actions */}
      {paymentPending && (
        <Button type="button" onClick={checkPaymentStatus} disabled={loading} className="w-full">
          Check payment status
        </Button>
      )}
      <div className="flex gap-3">
        {!hideBackButton && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1 h-12 bg-slate-100 hover:bg-slate-200 text-slate-900 border-slate-300 font-semibold"
            disabled={loading || paymentSuccess || paymentPending}
          >
            Back
          </Button>
        )}
        <Button
          type="submit"
          disabled={loading || !stripe || !elements || parentLoading || paymentSuccess || paymentPending}
          className={`${hideBackButton ? 'w-full' : 'flex-1'} h-12 font-semibold transition-colors ${
            paymentSuccess
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-pink-600 hover:bg-pink-700'
          } text-white`}
        >
          {paymentSuccess ? (
            <>
              <Check className="w-5 h-5 mr-2" />
              Payment Successful
            </>
          ) : loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Pay {price}
            </>
          )}
        </Button>
      </div>

      <p className="text-xs text-slate-500 text-center">Secure payment by Stripe.</p>
    </form>
  );
}

export default function OnboardingCheckout({
  lexiProPlan,
  userEmail,
  userPhone,
  stripeCustomerId,
  onSuccess,
  onAlreadyPurchased,
  onError,
  onClose,
  refreshJWT,
}) {
  const location = useLocation();
  const [isCheckoutInitializing, setIsCheckoutInitializing] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState("");
  const [checkoutError, setCheckoutError] = useState("");
  const [alreadyPurchased, setAlreadyPurchased] = useState(false);
  const [checkoutPriceSummary, setCheckoutPriceSummary] = useState(null);
  const [stripePromise, setStripePromise] = useState(initialStripePromise);
  const [publishableKey, setPublishableKey] = useState(initialPublishableKey);
  const [loadingMessage, setLoadingMessage] = useState("Preparing your checkout...");
  const hasInitialized = useRef(false);
  // The session refresh runs behind the card form
  const sessionRefresh = useRef(null);

  // Check if coming from a flow that should hide the back button (e.g., ?plan=pro or ?plan=day-pass)
  const searchParams = new URLSearchParams(location.search);
  const planParam = searchParams.get("plan");
  const hideBackButton = planParam === "pro" || planParam === "day-pass";

  // Pay waits for the refresh to settle, so it can't land after the refresh
  // that follows payment and store a token from before the purchase
  const waitForSessionRefresh = () => sessionRefresh.current;

  const startCheckout = async () => {
    if (!lexiProPlan) {
      logger.error("Lexi Pro plan not found");
      return;
    }

    // Prevent duplicate calls
    if (hasInitialized.current) {
      logger.log("Checkout already initialized, skipping");
      return;
    }

    hasInitialized.current = true;
    setCheckoutPriceSummary(lexiProPlan);
    setIsCheckoutInitializing(true);
    setCheckoutError("");

    // Rotating status messages
    const messages = [
      "Getting things ready...",
      "Securing your checkout...",
      "Loading payment details...",
      "Almost ready..."
    ];
    let messageIndex = 0;
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % messages.length;
      setLoadingMessage(messages[messageIndex]);
    }, 1500);

    try {
      const normalizedPhone = normalizePhoneForStripe(userPhone);

      logger.log("Starting checkout with Stripe customer ID:", stripeCustomerId || "none (creating new)");

      const response = await fetch("/api/stripe/create-in-app-subscription", {
        method: "POST",
        headers: getStripeSessionHeaders(),
        body: JSON.stringify({
          priceId: lexiProPlan.priceId,
          email: userEmail,
          phoneNumber: normalizedPhone,
          customerId: stripeCustomerId || undefined,
          termsAccepted: true,
          termsAcceptedAt: new Date().toISOString(),
        }),
      });

      const data = await response.json();
      // Paid already (a reload or second tab after paying): finish onboarding
      // instead of offering the plan again
      if (isAlreadyPurchased(response, data)) {
        setAlreadyPurchased(true);
        onAlreadyPurchased?.();
        return;
      }
      if (!response.ok) {
        throw new Error(data?.message || data?.error || "Unable to start checkout.");
      }

      logger.log("Checkout created, customer ID:", data.customerId, "created:", data.createdCustomer);

      if (data.publicKey && data.publicKey !== publishableKey) {
        setPublishableKey(data.publicKey);
        setStripePromise(loadStripe(data.publicKey));
      }

      // Checkout links ownership on the server before returning success, so the
      // card form shows now and the session refresh finishes while the buyer
      // types. A failed refresh doesn't stop payment.
      sessionRefresh.current = Promise.resolve(refreshJWT()).catch((error) => {
        logger.warn("Session refresh before payment failed:", error);
      });
      setCheckoutClientSecret(data.clientSecret);
    } catch (error) {
      logger.error("Error starting checkout:", error);
      setCheckoutError(error.message || "Unable to start checkout. Please try again.");
      hasInitialized.current = false; // Allow retry on error
    } finally {
      clearInterval(messageInterval);
      setIsCheckoutInitializing(false);
      setLoadingMessage("Preparing your checkout...");
    }
  };

  // Auto-start checkout on mount
  useEffect(() => {
    if (!hasInitialized.current && lexiProPlan) {
      startCheckout();
    }
  }, []);

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
      {isCheckoutInitializing ? (
        <div className="space-y-6">
          {/* Loading message */}
          <div className="text-center mb-6">
            <Loader2 className="w-12 h-12 mx-auto text-pink-600 animate-spin mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{loadingMessage}</h2>
            <p className="text-slate-600 text-sm">This will only take a moment</p>
          </div>

          <div className="animate-pulse space-y-6">
          {/* Header skeleton */}
          <div className="space-y-3">
            <div className="h-8 bg-slate-200 rounded w-3/4"></div>
            <div className="h-4 bg-slate-200 rounded w-1/2"></div>
          </div>

          {/* Price display skeleton */}
          <div className="border-t border-b border-slate-200 py-6 space-y-3">
            <div className="flex justify-between">
              <div className="h-5 bg-slate-200 rounded w-1/3"></div>
              <div className="h-5 bg-slate-200 rounded w-1/4"></div>
            </div>
            <div className="flex justify-between">
              <div className="h-4 bg-slate-200 rounded w-1/4"></div>
              <div className="h-4 bg-slate-200 rounded w-1/5"></div>
            </div>
          </div>

          {/* Form fields skeleton */}
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="h-4 bg-slate-200 rounded w-1/4"></div>
              <div className="h-12 bg-slate-200 rounded"></div>
            </div>
            <div className="space-y-2">
              <div className="h-4 bg-slate-200 rounded w-1/3"></div>
              <div className="h-12 bg-slate-200 rounded"></div>
            </div>
            <div className="space-y-2">
              <div className="h-4 bg-slate-200 rounded w-1/4"></div>
              <div className="h-12 bg-slate-200 rounded"></div>
            </div>
          </div>

          {/* Button skeleton */}
          <div className="h-12 bg-slate-200 rounded"></div>

          {/* Footer text skeleton */}
          <div className="space-y-2">
            <div className="h-3 bg-slate-200 rounded w-full"></div>
            <div className="h-3 bg-slate-200 rounded w-5/6"></div>
          </div>
          </div>
        </div>
      ) : alreadyPurchased ? (
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 mx-auto text-pink-600 animate-spin" />
          <h2 className="text-2xl font-bold text-slate-900">{ALREADY_PURCHASED}</h2>
        </div>
      ) : checkoutError ? (
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Checkout Error</h2>
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 p-4 rounded-lg">
              {checkoutError}
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              type="button"
              onClick={onClose}
              className="flex-1 h-12 bg-slate-100 hover:bg-slate-200 text-slate-900 font-semibold"
            >
              Back to Plans
            </Button>
            <Button
              type="button"
              onClick={() => {
                hasInitialized.current = false;
                startCheckout();
              }}
              className="flex-1 h-12 bg-pink-600 hover:bg-pink-700 text-white font-semibold"
            >
              Try Again
            </Button>
          </div>
        </div>
      ) : checkoutClientSecret && stripePromise && checkoutPriceSummary ? (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Complete Your Purchase</h2>
            <p className="text-slate-600 text-base">
              {checkoutPriceSummary.priceType === "one_time"
                ? `Purchase ${checkoutPriceSummary.productName} for ${formatSubscriptionPrice(checkoutPriceSummary.amount, checkoutPriceSummary.currency?.toUpperCase?.() || "AUD")}`
                : `Subscribe to ${checkoutPriceSummary.productName} for ${formatSubscriptionPrice(checkoutPriceSummary.amount, checkoutPriceSummary.currency?.toUpperCase?.() || "AUD")}/month`
              }
            </p>
          </div>

          <Elements
            stripe={stripePromise}
            options={{
              clientSecret: checkoutClientSecret,
              appearance: { theme: "stripe" },
            }}
          >
            <CheckoutForm
              clientSecret={checkoutClientSecret}
              email={userEmail}
              productName={checkoutPriceSummary.priceType === "one_time"
                ? checkoutPriceSummary.productName
                : `${checkoutPriceSummary.productName} Monthly Subscription`
              }
              price={checkoutPriceSummary.priceType === "one_time"
                ? formatSubscriptionPrice(checkoutPriceSummary.amount, checkoutPriceSummary.currency?.toUpperCase?.() || "AUD")
                : formatSubscriptionPrice(checkoutPriceSummary.amount, checkoutPriceSummary.currency?.toUpperCase?.() || "AUD") + "/month"
              }
              onSuccess={onSuccess}
              onError={onError}
              onCancel={onClose}
              isLoading={false}
              hideBackButton={hideBackButton}
              beforeConfirm={waitForSessionRefresh}
            />
          </Elements>
        </div>
      ) : null}
    </div>
  );
}

